// ============================================================
// FONADHOO CRM v3.0 - ENHANCED OUTREACH
// ============================================================
// Original layout & server-side filtering restored exactly.
// Changes from original:
//   1. Native <select> dropdowns → SearchableSelect
//   2. Voter-ID number input in Add modal → SearchableSelect
//   3. Stats cards are now clickable — tapping one sets a
//      client-side post-filter so the timeline below shows
//      only the records that make up that stat.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { OUTREACH_TYPES, OUTREACH_OUTCOMES } from './shared/constants';
import { formatDate, formatRelativeTime } from './shared/utils';
import SearchableSelect from './shared/SearchableSelect';
import { Icon } from './shared/Icon';

export default function EnhancedOutreach({ supabase, profile, user, allVoters = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = user || profile;
  const isPatchAgent = currentUser?.role === 'patch_agent';

  const [outreach, setOutreach]   = useState([]);
  const [supportHistory, setSupportHistory] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);

  // Stat-card post-filter: null | 'successful' | 'commitments' | 'unique'
  const [statFilter, setStatFilter] = useState(location.state?.statFilter || null);

  // Filters — same state variables as the original
  const [filterType,       setFilterType]       = useState('All');
  const [filterOutcome,    setFilterOutcome]     = useState('All');
  const [filterDateRange,  setFilterDateRange]   = useState('All');
  const [filterTeamMember, setFilterTeamMember]  = useState(location.state?.memberId || 'All');
  const [searchVoter,      setSearchVoter]       = useState('');
  const [expandedIslands, setExpandedIslands] = useState(() => new Set());
  const toggleIsland = (key) => setExpandedIslands(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const [expandedVoters, setExpandedVoters] = useState(() => new Set());
  const toggleVoter = (key) => setExpandedVoters(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Add modal
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    voter_id: '',
    type: 'In-Person Visit (Home)',
    outcome: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });

  // ─── Original data-loading (unchanged) ───────────────────────
  useEffect(() => {
    loadOutreach();
    loadTeamMembers();
  }, [filterType, filterOutcome, filterDateRange, filterTeamMember, searchVoter]);

  // Load support_history once (used to derive Withdrawals: voters whose
  // latest support change was from supporter → non-supporter)
  useEffect(() => {
    supabase.from('support_history')
      .select('voter_id, changed_from, changed_to, changed_at')
      .order('changed_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('[outreach] support_history load failed:', error);
        setSupportHistory(data || []);
      });
  }, [supabase]);

  const loadOutreach = async () => {
    setLoading(true);

    let query = supabase
      .from('outreach')
      .select(`
        *,
        voter:voters(id, name, island, ward),
        logged_by_profile:profiles(full_name)
      `);

    if (filterType !== 'All')       query = query.eq('type', filterType);
    if (filterOutcome !== 'All')    query = query.eq('outcome', filterOutcome);
    // patch_agent always sees only their own outreach — team filter locked server-side
    if (isPatchAgent)               query = query.eq('logged_by', currentUser.id);
    else if (filterTeamMember !== 'All') query = query.eq('logged_by', filterTeamMember);

    if (filterDateRange !== 'All') {
      const now = new Date();
      let startDate;
      if (filterDateRange === 'Today') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (filterDateRange === 'Week') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (filterDateRange === 'Month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      if (startDate) query = query.gte('date', startDate.toISOString().split('T')[0]);
    }

    const { data } = await query.order('date', { ascending: false }).limit(200);

    let filtered = data || [];
    if (searchVoter) {
      filtered = filtered.filter(o =>
        o.voter?.name?.toLowerCase().includes(searchVoter.toLowerCase())
      );
    }

    setOutreach(filtered);
    setLoading(false);
    setStatFilter(null); // reset stat highlight when data reloads
  };

  const loadTeamMembers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('is_active', true)
      .order('full_name');
    setTeamMembers(data || []);
  };

  // ─── Original add handler (unchanged) ────────────────────────
  const handleAddOutreach = async () => {
    if (!addForm.voter_id) {
      alert('Please select a voter');
      return;
    }
    const { error } = await supabase.from('outreach').insert({
      ...addForm,
      voter_id: parseInt(addForm.voter_id),
      logged_by: currentUser?.id,
    });
    if (!error) {
      // Commitment Made auto-upgrades the voter's support level to Strong Support
      if (addForm.outcome === 'Commitment Made') {
        await supabase.from('voters').update({ support: 'Strong Support' }).eq('id', parseInt(addForm.voter_id));
      }
      setShowAdd(false);
      setAddForm({
        voter_id: '',
        type: 'In-Person Visit (Home)',
        outcome: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
      loadOutreach();
    }
  };

  const getOutcomeColor = (outcome) => {
    if (outcome?.includes('Commitment')) return '#10b981';
    if (outcome?.includes('Conversation') || outcome?.includes('Discussed')) return '#3b82f6';
    if (outcome?.includes('No Answer') || outcome?.includes('Busy')) return '#f59e0b';
    if (outcome?.includes('Refused') || outcome?.includes('Hostile')) return '#ef4444';
    return 'var(--text-3)';
  };

  // ─── Stat card values (same derivation as original) ──────────
  const negatives = ['No Answer / Not Home', 'Busy / Call Back Later', 'Refused to Talk'];
  // Voters who currently have a Commitment Made outreach record (= currently committed)
  const committedVoterIds = useMemo(() => {
    const s = new Set();
    for (const o of outreach) if (o.outcome === 'Commitment Made') s.add(o.voter_id);
    return s;
  }, [outreach]);

  // Voters who withdrew — derived from support_history (latest support change
  // for that voter was supporter → non-supporter, and they aren't currently committed)
  const SUPPORTER_LEVELS = ['Strong Support', 'Leaning Support'];
  const withdrawnVoterIds = useMemo(() => {
    const latestPerVoter = new Map(); // voter_id → most recent support_history row
    for (const h of supportHistory) {
      if (!latestPerVoter.has(h.voter_id)) latestPerVoter.set(h.voter_id, h);
    }
    const ids = new Set();
    for (const [vid, h] of latestPerVoter) {
      const wasSupporter = SUPPORTER_LEVELS.includes(h.changed_from);
      const isSupporter  = SUPPORTER_LEVELS.includes(h.changed_to);
      if (wasSupporter && !isSupporter && !committedVoterIds.has(vid)) ids.add(vid);
    }
    return ids;
  }, [supportHistory, committedVoterIds]);

  // For each currently-committed voter, find their most recent Commitment Made
  // outreach record (used for the Commitments filter list).
  const latestCommitmentByVoter = useMemo(() => {
    const ts = (x) => x?.created_at || x?.date || '';
    const map = new Map();
    for (const o of outreach) {
      if (o.outcome !== 'Commitment Made') continue;
      const existing = map.get(o.voter_id);
      if (!existing || ts(o) > ts(existing)) map.set(o.voter_id, o);
    }
    return map;
  }, [outreach]);

  const statValues = {
    total:       outreach.length,
    successful:  outreach.filter(o => !negatives.includes(o.outcome)).length,
    commitments: committedVoterIds.size,
    withdrawn:   withdrawnVoterIds.size,
    unique:      new Set(outreach.map(o => o.voter_id)).size,
  };

  // ─── Client-side post-filter driven by clicked stat card ─────
  const displayedOutreach = useMemo(() => {
    if (!statFilter)                   return outreach;
    if (statFilter === 'successful')   return outreach.filter(o => !negatives.includes(o.outcome));
    if (statFilter === 'failed')       return outreach.filter(o => negatives.includes(o.outcome));
    if (statFilter === 'commitments') {
      return [...latestCommitmentByVoter.values()];
    }
    if (statFilter === 'withdrawn') {
      // Synthesize one row per withdrawn voter from support_history so the list
      // can be rendered with the existing card UI. These aren't outreach
      // records — they're audit events.
      const latestHistByVoter = new Map();
      for (const h of supportHistory) {
        if (!latestHistByVoter.has(h.voter_id) && withdrawnVoterIds.has(h.voter_id)) {
          latestHistByVoter.set(h.voter_id, h);
        }
      }
      // Resolve voter info from allVoters (full voter list passed as prop).
      // Fall back to outreach.voter if needed (shouldn't usually happen).
      const voterById = new Map();
      for (const v of (allVoters || [])) voterById.set(v.id, v);
      for (const o of outreach) {
        if (o.voter && !voterById.has(o.voter_id)) voterById.set(o.voter_id, o.voter);
      }
      return [...latestHistByVoter.values()].map(h => ({
        id: `wh-${h.voter_id}-${h.changed_at}`,
        voter_id: h.voter_id,
        voter: voterById.get(h.voter_id) || null,
        outcome: 'Commitment Withdrawn',
        type: 'Support Level Change',
        date: (h.changed_at || '').split('T')[0],
        notes: `Support changed from ${h.changed_from || 'Undecided'} to ${h.changed_to || 'Undecided'}.`,
        _isSynthetic: true,
      }));
    }
    if (statFilter === 'unique') {
      // One record per voter (most recent).
      const seen = new Set();
      return outreach.filter(o => {
        if (seen.has(o.voter_id)) return false;
        seen.add(o.voter_id);
        return true;
      });
    }
    return outreach;
  }, [outreach, statFilter, latestCommitmentByVoter, supportHistory, withdrawnVoterIds, allVoters]);

  // ─── Voter options for SearchableSelect in Add modal ─────────
  const voterOptions = useMemo(
    () => allVoters
      .map(v => ({ value: String(v.id), label: `${v.name}${v.island ? ` — ${v.island}` : ''}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [allVoters]
  );

  // ─── Shared select style passed to SearchableSelect trigger ──
  const selectStyle = {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid var(--border-2)',
    background: 'var(--surface-2)',
    fontSize: '13px',
  };

  // Stat card helper — returns style based on whether it's active.
  const statCardStyle = (key, color) => ({
    background: 'var(--surface-2)',
    padding: '14px 16px',
    borderRadius: '12px',
    cursor: 'pointer',
    border: statFilter === key ? `1px solid ${color}` : '1px solid var(--border)',
    boxShadow: statFilter === key ? `0 0 0 1px ${color}` : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
    minHeight: 96,
    position: 'relative', overflow: 'hidden',
  });

  const toggleStat = (key) => setStatFilter(f => f === key ? null : key);

  // ────────────────────────────────────────────────────────────────
  // RENDER — original layout
  // ────────────────────────────────────────────────────────────────
  return (
    <div>
      {(location.state?.memberId || location.state?.statFilter || location.state?.from) && (
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginBottom: 10, padding: '7px 14px',
            borderRadius: 7, border: '1px solid var(--border)',
            background: 'var(--surface)', color: 'var(--link)',
            fontSize: 13, cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          ← Back
        </button>
      )}
      {/* Header */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        borderRadius: '14px',
        padding: '20px',
        marginBottom: '20px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'nowrap',
          marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6', flexShrink: 0 }}>
              <Icon name="phone" size={20} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Field Contact</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Outreach</h2>
            </div>
          </div>
          <button
            onClick={() => setShowAdd(!showAdd)}
            style={{
              padding: '10px 18px',
              borderRadius: '9px',
              border: 'none',
              background: showAdd ? 'var(--border-2)' : '#3b82f6',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              display: 'inline-flex', alignItems: 'center', gap: 6,
              flexShrink: 0, whiteSpace: 'nowrap',
              transition: 'filter 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.12)'}
            onMouseLeave={e => e.currentTarget.style.filter = 'none'}
          >
            {showAdd ? 'Cancel' : <><Icon name="plus" size={15} color="#fff" /> Log Outreach</>}
          </button>
        </div>

        {/* Stats Overview — clickable */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '12px',
          marginBottom: '20px',
        }}>
          {[
            { key: null,          label: 'Reach Attempts',   value: statValues.total,       color: '#3b82f6', icon: 'phone' },
            { key: 'successful',  label: 'Successful',       value: statValues.successful,  color: '#10b981', icon: 'check' },
            { key: 'commitments', label: 'Commitments',      value: statValues.commitments, color: '#f59e0b', icon: 'target' },
            { key: 'withdrawn',   label: 'Withdrawals',      value: statValues.withdrawn,   color: '#ef4444', icon: 'x' },
            { key: 'unique',      label: 'Unique Voters',    value: statValues.unique,      color: '#64748b', icon: 'users' },
          ].map(({ key, label, value, color, icon }) => {
            const on = statFilter === key;
            return (
              <div key={String(key)} onClick={() => toggleStat(key)} style={statCardStyle(key, color)}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = on ? color : 'var(--border)'; }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                  <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                    <Icon name={icon} size={15} />
                  </span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, opacity: on ? 1 : 0, transition: 'opacity 0.15s' }} />
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
        }}>
          <input
            type="text"
            placeholder="Search voter name..."
            value={searchVoter}
            onChange={(e) => setSearchVoter(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border-2)',
              background: 'var(--surface-2)',
              color: 'var(--text)',
              fontSize: '14px',
              outline: 'none',
            }}
          />

          <SearchableSelect
            value={filterType === 'All' ? '' : filterType}
            options={OUTREACH_TYPES}
            onChange={v => setFilterType(v || 'All')}
            placeholder="All Types"
            style={selectStyle}
          />

          <SearchableSelect
            value={filterOutcome === 'All' ? '' : filterOutcome}
            options={OUTREACH_OUTCOMES}
            onChange={v => setFilterOutcome(v || 'All')}
            placeholder="All Outcomes"
            style={selectStyle}
          />

          <SearchableSelect
            value={filterDateRange === 'All' ? '' : filterDateRange}
            options={[
              { value: 'Today', label: 'Today' },
              { value: 'Week',  label: 'Last 7 Days' },
              { value: 'Month', label: 'This Month' },
            ]}
            onChange={v => setFilterDateRange(v || 'All')}
            placeholder="All Time"
            style={selectStyle}
          />

          {!isPatchAgent && (
            <SearchableSelect
              value={filterTeamMember === 'All' ? '' : filterTeamMember}
              options={teamMembers.map(m => ({ value: m.id, label: m.full_name }))}
              onChange={v => setFilterTeamMember(v || 'All')}
              placeholder="All Team Members"
              style={selectStyle}
            />
          )}
        </div>
      </div>

      {/* Add Outreach Modal */}
      {showAdd && (
        <div style={{
          background: 'var(--surface-2)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: '2px solid #3b82f6',
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            Log New Outreach
          </h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                Voter (required)
              </label>
              <SearchableSelect
                value={addForm.voter_id}
                options={voterOptions}
                onChange={v => setAddForm({ ...addForm, voter_id: v || '' })}
                placeholder="Search voter by name…"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                  Contact Type
                </label>
                <SearchableSelect
                  value={addForm.type}
                  options={OUTREACH_TYPES}
                  onChange={v => setAddForm({ ...addForm, type: v || OUTREACH_TYPES[0] })}
                  placeholder="Select type…"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                  Outcome
                </label>
                <SearchableSelect
                  value={addForm.outcome}
                  options={OUTREACH_OUTCOMES}
                  onChange={v => setAddForm({ ...addForm, outcome: v || '' })}
                  placeholder="Select outcome…"
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                Date
              </label>
              <input
                type="date"
                value={addForm.date}
                onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-2)',
                  background: 'var(--surface-2)',
                  color: 'var(--text)',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                Notes
              </label>
              <textarea
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                rows={3}
                placeholder="What was discussed? Any follow-ups needed?"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-2)',
                  background: 'var(--surface-2)',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleAddOutreach}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Save Outreach Log
              </button>
              <button
                onClick={() => {
                  setShowAdd(false);
                  setAddForm({
                    voter_id: '',
                    type: 'In-Person Visit (Home)',
                    outcome: '',
                    notes: '',
                    date: new Date().toISOString().split('T')[0],
                  });
                }}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-2)',
                  background: 'transparent',
                  color: 'var(--text-2)',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stat-filter banner */}
      {statFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--border)',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          padding: '8px 14px',
          marginBottom: '16px',
          fontSize: '12px',
          color: 'var(--link)',
        }}>
          <span>
            Showing {displayedOutreach.length} record{displayedOutreach.length !== 1 ? 's' : ''} —{' '}
            {statFilter === 'successful'  && 'Successful contacts'}
            {statFilter === 'failed'      && 'Failed contacts'}
            {statFilter === 'commitments' && 'Commitments only'}
            {statFilter === 'withdrawn'   && 'Withdrawn commitments only'}
            {statFilter === 'unique'      && 'One record per unique voter'}
          </span>
          <button
            onClick={() => setStatFilter(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--link)',
              cursor: 'pointer',
              fontSize: '14px',
              lineHeight: 1,
              padding: '0 4px',
            }}
            title="Clear stat filter"
          >
            ×
          </button>
        </div>
      )}

      {/* Outreach Timeline */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-2)' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-2)',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          Loading outreach...
        </div>
      ) : (() => {
        // Group outreach by voter island. Entries with no voter or no island → "__no_island__"
        const grouped = {};
        for (const entry of displayedOutreach) {
          const key = entry.voter?.island || '__no_island__';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(entry);
        }
        // Sort islands by their newest entry (most recent activity at top)
        const islandTs = (k) => grouped[k].reduce((max, e) => {
          const t = e.created_at || e.date || '';
          return t > max ? t : max;
        }, '');
        const islands = Object.keys(grouped)
          .filter(k => k !== '__no_island__')
          .sort((a, b) => islandTs(b).localeCompare(islandTs(a)));
        const sections = [...islands, ...(grouped['__no_island__'] ? ['__no_island__'] : [])];

        const renderEntryCard = (entry) => (
            <div
              key={entry.id}
              style={{
                background: 'var(--surface-2)',
                borderRadius: '12px',
                padding: '20px',
                borderLeft: `4px solid ${getOutcomeColor(entry.outcome)}`,
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
              }}>
                {/* Main content */}
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    {entry.voter ? (
                      <span onClick={() => navigate(`/voter/${entry.voter.id}`)}
                        style={{ cursor: 'pointer', color: 'var(--link)', textDecoration: 'none' }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                        {entry.voter.name}
                      </span>
                    ) : (entry.voter?.name || 'Unknown Voter')}
                  </h4>

                  {entry.voter && (
                    <div style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '12px' }}>
                      📍 {entry.voter.island}, {entry.voter.ward}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: '#3b82f620',
                      color: '#3b82f6',
                    }}>
                      {entry.type}
                    </span>

                    {entry.outcome && (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: getOutcomeColor(entry.outcome) + '20',
                        color: getOutcomeColor(entry.outcome),
                      }}>
                        {entry.outcome}
                      </span>
                    )}
                  </div>

                  {entry.notes && (
                    <p style={{ fontSize: '14px', color: 'var(--text)', lineHeight: '1.6' }}>
                      {entry.notes}
                    </p>
                  )}
                </div>

                {/* Meta info */}
                <div style={{ textAlign: 'right', fontSize: '12px', color: 'var(--text-3)', minWidth: '120px' }}>
                  <div style={{ marginBottom: '4px' }}>{formatDate(entry.date)}</div>
                  <div style={{ marginBottom: '4px' }}>{formatRelativeTime(entry.date)}</div>
                  {entry.logged_by_profile?.full_name && (
                    <div style={{ marginTop: '8px', color: 'var(--text-2)' }}>
                      By: {entry.logged_by_profile.full_name}
                    </div>
                  )}
                </div>
              </div>
            </div>
        );

        // Within each island, sub-group entries by voter
        const groupByVoter = (entries) => {
          const byVoter = {};
          for (const e of entries) {
            const vk = e.voter?.id ? `v_${e.voter.id}` : '__no_voter__';
            if (!byVoter[vk]) byVoter[vk] = { voter: e.voter, entries: [] };
            byVoter[vk].entries.push(e);
          }
          // Sort each voter's entries newest first by created_at (falls back to date)
          const ts = (x) => x?.created_at || x?.date || '';
          Object.values(byVoter).forEach(g => g.entries.sort((a, b) => ts(b).localeCompare(ts(a))));
          // Sort voters by their newest entry (most recent activity at top)
          return Object.entries(byVoter).sort((a, b) =>
            ts(b[1].entries[0]).localeCompare(ts(a[1].entries[0]))
          );
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sections.map(key => {
              const isNoIsland = key === '__no_island__';
              const isCollapsed = !expandedIslands.has(key);
              const headerLabel = isNoIsland ? '📍 Unspecified Island' : `📍 ${key}`;
              const voterGroups = groupByVoter(grouped[key]);
              return (
                <div key={key}>
                  {(() => {
                    const islandFails = grouped[key].filter(o => negatives.includes(o.outcome)).length;
                    const islandSuccess = grouped[key].length - islandFails;
                    return (
                      <div onClick={() => toggleIsland(key)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCollapsed ? 0 : 10, cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isNoIsland ? '#ef4444' : 'var(--link)', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>
                          {headerLabel}
                        </span>
                        <div style={{ flex: 1, height: 1, background: 'var(--border)', minWidth: 20 }} />
                        <span style={{ fontSize: 11, color: 'var(--border-2)' }}>{voterGroups.length} voter{voterGroups.length !== 1 ? 's' : ''}</span>
                        <span style={{ fontSize: 11, color: '#3b82f6', whiteSpace: 'nowrap' }}>{grouped[key].length} attempts</span>
                        <span
                          onClick={e => { e.stopPropagation(); setStatFilter(statFilter === 'successful' ? null : 'successful'); }}
                          title="Filter successful contacts"
                          style={{ fontSize: 11, color: '#10b981', whiteSpace: 'nowrap', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, background: statFilter === 'successful' ? '#10b98120' : 'transparent', border: statFilter === 'successful' ? '1px solid #10b981' : '1px solid transparent' }}>
                          ✓ {islandSuccess}
                        </span>
                        <span
                          onClick={e => { e.stopPropagation(); setStatFilter(statFilter === 'failed' ? null : 'failed'); }}
                          title="Filter failed contacts"
                          style={{ fontSize: 11, color: '#ef4444', whiteSpace: 'nowrap', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, background: statFilter === 'failed' ? '#ef444420' : 'transparent', border: statFilter === 'failed' ? '1px solid #ef4444' : '1px solid transparent' }}>
                          ✗ {islandFails}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--border-2)', marginLeft: 4 }}>{isCollapsed ? '▶' : '▼'}</span>
                      </div>
                    );
                  })()}
                  {!isCollapsed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {voterGroups.map(([vkey, g]) => {
                        const voterCollapsed = !expandedVoters.has(vkey);
                        const latest = g.entries[0];
                        const latestColor = getOutcomeColor(latest?.outcome);
                        return (
                          <div key={vkey} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                            <div onClick={() => toggleVoter(vkey)}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }}>
                              <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{voterCollapsed ? '▶' : '▼'}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span onClick={e => { e.stopPropagation(); g.voter && navigate(`/voter/${g.voter.id}`); }}
                                  style={{ fontSize: 14, fontWeight: 600, color: g.voter ? 'var(--link)' : 'var(--text)', cursor: g.voter ? 'pointer' : 'default' }}
                                  onMouseEnter={e => { if (g.voter) e.currentTarget.style.textDecoration = 'underline'; }}
                                  onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}>
                                  {g.voter?.name || 'Unknown voter'}
                                </span>
                                {g.voter?.ward && <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 8 }}>{g.voter.ward}</span>}
                              </div>
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: latestColor + '20', color: latestColor }}>
                                Latest: {latest?.outcome || 'No outcome'}
                              </span>
                              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{g.entries.length} attempt{g.entries.length !== 1 ? 's' : ''}</span>
                            </div>
                            {!voterCollapsed && (
                              <div style={{ borderTop: '1px solid var(--border)', padding: '10px', display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface)' }}>
                                {g.entries.map(renderEntryCard)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {!loading && outreach.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-3)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📞</div>
          <p>No outreach records found</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            {searchVoter || filterType !== 'All' || filterOutcome !== 'All'
              ? 'Try adjusting your filters'
              : 'Click "Log Outreach" to start tracking contacts'}
          </p>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
